/**
 * Company Strategy
 * 
 * This strategy handles the company topic, including analyzing company requirements
 * and providing recommendations.
 * 
 * This implementation uses a closure to track its own state and phase transitions.
 * 
 * Updated to use centralized ICP requirements repository.
 */

import { runAIMatches, runMatchesAnalyze } from '../api';
import generateEnhancedGuidance from './enhancedGuidance';
import { createBaseStrategy, StrategyPhase } from './baseStrategy';
import { handleUnclearInput } from './clarifyingQuestions';

/**
 * Handler for the init phase of the company strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing company discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Generate default guidance for company topic
  const defaultGuidance = `Focus on understanding the company culture and values. Ask about the company's mission, the team structure, and what makes someone successful in their organization. Probe for information about the company's stage, size, and industry.`;
  
  // Update decision to move to the suggestions phase
  return {
    ...decision,
    actionType: 'SEND_TO_BACKEND',
    agentGuidance: defaultGuidance,
    strategyPhase: StrategyPhase.SUGGESTIONS
  };
};

/**
 * Handler for the suggestions phase of the company strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating company suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Run AI matches to get company recommendations
    const aiMatchesResult = await runAIMatches(messages);
    
    if (!aiMatchesResult) {
      throw new Error('Failed to get AI matches');
    }
    
    // Handle unclear inputs using shared utility with copilot-powered questions
    const userMessages = messages.filter(msg => msg.role === 'User');
    const latestUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1] : null;
    const unclearInputResult = await handleUnclearInput(aiMatchesResult, 'company', latestUserMessage, setMessages, decision);
    if (unclearInputResult) {
      return {
        ...unclearInputResult,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    }
    
    // Parse icp_requirements from the AI matches result
    if (aiMatchesResult.search_gen_response && aiMatchesResult.search_gen_response.icp_requirements) {
      const icpRequirements = aiMatchesResult.search_gen_response.icp_requirements;
      
      // Log the icp_requirements for debugging
      console.log('Company ICP Requirements:', icpRequirements);
      
      // Check for company requirements
      const companyRequirements = icpRequirements.filter(req => 
        req.section_name.includes("Company") || 
        req.section_name.includes("Industry")
      );
      
      if (companyRequirements.length > 0) {
        // Log company requirements
        console.log('Company Requirements:', companyRequirements);
        
        // Update thinking accordion with company requirements
        if (setMessages) {
          setMessages(prev => {
            const updatedMessages = prev.map(msg => 
              msg.role === 'AI' && msg.content === 'Typing...' 
                ? { 
                    ...msg, 
                    thinking: `${msg.thinking}\nFound company requirements: ${companyRequirements.map(req => req.bool_str).join(', ')}` 
                  }
                : msg
            );
            return updatedMessages;
          });
        }
        
        // Update centralized ICP requirements if updateRequirements is available
        if (decision.updateRequirements) {
          console.log('Updating centralized ICP requirements with company requirements');
          decision.updateRequirements(companyRequirements, 'company');
        }
      }
    }
    
    // Generate enhanced agent guidance with Findem Magic explanations
    const enhancedGuidance = await generateEnhancedGuidance(
      aiMatchesResult, 
      decision, 
      'company'
    );
    
    // Check for Findem Magic requirements in the AI matches result
    if (aiMatchesResult.search_gen_response && aiMatchesResult.search_gen_response.icp_requirements) {
      const findemMagic = aiMatchesResult.search_gen_response.icp_requirements.find(req => 
        req.section_name === "Findem Magic"
      );
      
      if (findemMagic && decision.updateRequirements) {
        console.log('Updating centralized ICP requirements with Findem Magic requirements');
        decision.updateRequirements([findemMagic], 'findem_magic');
      }
    }
    
    // Update decision with AI matches result and enhanced guidance
    return {
      ...decision,
      actionType: 'SEND_WITH_GUIDANCE',
      agentGuidance: enhancedGuidance,
      aiMatchesResult,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  } catch (error) {
    console.error('Error generating company suggestions:', error);
    
    // Fall back to conversation phase without suggestions
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the company strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about company...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Generate specific guidance for the company conversation phase
  const companyConversationGuidance = `
IMPORTANT: The current topic is COMPANY & INDUSTRY. Focus ONLY on discussing company and industry context for this role.

1. First, acknowledge any company information the user has provided and ask for more details:
   - What is the company's mission and values?
   - What is the team structure like?
   - What makes someone successful in their organization?
   - What industry or sector is the company in?

2. Then, explore other aspects of the company that might be relevant:
   - What is the company's stage (startup, growth, enterprise)?
   - What is the company's size?
   - What is the company culture like?
   - Are there any specific industry challenges or opportunities?

3. After gathering sufficient information, summarize the company context clearly:
   - "Based on our conversation, it sounds like your company is [summarize company context]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that summary of your company and industry context sound accurate? If so, shall we move on to discussing education and certifications?"
   - "Have I captured your company information correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the company discussion.
`;
  
  // In the conversation phase, we pass the user's message to the backend with specific company guidance
  return {
    ...decision,
    actionType: 'SEND_TO_BACKEND',
    agentGuidance: companyConversationGuidance,
    strategyPhase: StrategyPhase.CONVERSATION
  };
};

/**
 * Create a company strategy that tracks its own state
 * This approach uses a closure to maintain state between calls
 */
const createCompanyStrategy = () => {
  // Internal state tracking
  let hasExecutedInitPhase = false;
  let hasExecutedSuggestionsPhase = false;
  
  // The actual strategy function
  return async (decision, messages, setMessages) => {
    console.log('Company strategy executing with state:', { 
      hasExecutedInitPhase, 
      hasExecutedSuggestionsPhase,
      incomingPhase: decision.strategyPhase
    });
    
    // Update thinking accordion with the current state
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nCompany strategy state: ${JSON.stringify({
                  hasExecutedInitPhase,
                  hasExecutedSuggestionsPhase,
                  incomingPhase: decision.strategyPhase
                })}`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // If a specific phase is requested in the decision, honor it
    if (decision.strategyPhase === StrategyPhase.INIT) {
      console.log('Explicitly requested INIT phase');
      hasExecutedInitPhase = true;
      return await initPhaseHandler(decision, messages, setMessages);
    }
    else if (decision.strategyPhase === StrategyPhase.SUGGESTIONS) {
      console.log('Explicitly requested SUGGESTIONS phase');
      hasExecutedSuggestionsPhase = true;
      return await suggestionsPhaseHandler(decision, messages, setMessages);
    }
    else if (decision.strategyPhase === StrategyPhase.CONVERSATION) {
      console.log('Explicitly requested CONVERSATION phase');
      return await conversationPhaseHandler(decision, messages, setMessages);
    }
    
    // Otherwise, determine which phase to execute based on internal state
    if (!hasExecutedInitPhase) {
      console.log('Executing init phase for first time');
      hasExecutedInitPhase = true;
      return await initPhaseHandler(decision, messages, setMessages);
    } 
    else if (!hasExecutedSuggestionsPhase) {
      console.log('Executing suggestions phase for first time');
      hasExecutedSuggestionsPhase = true;
      return await suggestionsPhaseHandler(decision, messages, setMessages);
    }
    else {
      console.log('Executing conversation phase');
      return await conversationPhaseHandler(decision, messages, setMessages);
    }
  };
};

// Create and export the company strategy
const companyStrategy = createCompanyStrategy();
export default companyStrategy;
